import { useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  Code2,
  FileText,
  Presentation,
  PenLine,
  Search,
  BookOpen,
  Layers,
  Check,
} from 'lucide-react'
import type { ToolTemplate } from '@/types/contextEngine'
import { DOMAIN_LABELS } from '@/types/contextEngine'

// ─── Category display ────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<
  ToolTemplate['category'],
  { icon: typeof Code2; label: string; color: string }
> = {
  code: { icon: Code2, label: 'Code', color: 'text-blue-400 bg-blue-400/10' },
  design: { icon: Layers, label: 'Design', color: 'text-purple-400 bg-purple-400/10' },
  presentation: { icon: Presentation, label: 'Presentation', color: 'text-emerald-400 bg-emerald-400/10' },
  copy: { icon: PenLine, label: 'Copy', color: 'text-rose-400 bg-rose-400/10' },
  research: { icon: Search, label: 'Research', color: 'text-cyan-400 bg-cyan-400/10' },
  documentation: { icon: BookOpen, label: 'Docs', color: 'text-amber-400 bg-amber-400/10' },
  general: { icon: FileText, label: 'General', color: 'text-text-secondary bg-bg-tertiary' },
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface TemplateBrowserProps {
  templates: ToolTemplate[]
  selectedId: string | null
  onSelect: (template: ToolTemplate) => void
}

export function TemplateBrowser({ templates, selectedId, onSelect }: TemplateBrowserProps) {
  const enabledTemplates = useMemo(
    () => templates.filter((t) => t.enabled),
    [templates],
  )

  const disabledTemplates = useMemo(
    () => templates.filter((t) => !t.enabled),
    [templates],
  )

  return (
    <div className="space-y-3">
      <h2 className="font-display text-base font-semibold text-text-primary">
        Choose a Template
      </h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {enabledTemplates.map((template) => (
          <TemplateCard
            key={template.templateId}
            template={template}
            isSelected={selectedId === template.templateId}
            onSelect={onSelect}
          />
        ))}
        {disabledTemplates.map((template) => (
          <TemplateCard
            key={template.templateId}
            template={template}
            isSelected={false}
            onSelect={onSelect}
            disabled
          />
        ))}
      </div>
    </div>
  )
}

// ─── Template card ───────────────────────────────────────────────────────────

interface TemplateCardProps {
  template: ToolTemplate
  isSelected: boolean
  onSelect: (template: ToolTemplate) => void
  disabled?: boolean
}

function TemplateCard({ template, isSelected, onSelect, disabled }: TemplateCardProps) {
  const cat = CATEGORY_CONFIG[template.category]
  const CatIcon = cat.icon
  const domainLabels = template.selection.includeDomains
    .slice(0, 4)
    .map((d) => DOMAIN_LABELS[d])

  const extraCount = template.selection.includeDomains.length - 4

  return (
    <motion.button
      onClick={() => !disabled && onSelect(template)}
      disabled={disabled}
      whileTap={disabled ? undefined : { scale: 0.98 }}
      className={`relative flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors duration-200 ${
        disabled
          ? 'cursor-not-allowed border-border/50 bg-bg-elevated/50 opacity-50'
          : isSelected
            ? 'border-accent-amber bg-accent-amber/5 ring-1 ring-accent-amber/30'
            : 'border-border bg-bg-elevated hover:border-border-hover'
      }`}
    >
      {/* Selected check */}
      {isSelected && (
        <div className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-accent-amber">
          <Check size={12} className="text-bg-deepest" />
        </div>
      )}

      {/* Tool name + category */}
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs ${cat.color}`}>
          <CatIcon size={12} />
          {cat.label}
        </span>
        {disabled && (
          <span className="text-xs text-text-tertiary">Coming soon</span>
        )}
      </div>

      <h3 className="font-display text-sm font-semibold text-text-primary">
        {template.toolName}
      </h3>

      {/* Domain scope */}
      <div className="flex flex-wrap gap-1">
        {domainLabels.map((label) => (
          <span
            key={label}
            className="rounded-sm bg-bg-surface px-1.5 py-0.5 text-[11px] font-mono text-text-tertiary"
          >
            {label}
          </span>
        ))}
        {extraCount > 0 && (
          <span className="rounded-sm bg-bg-surface px-1.5 py-0.5 text-[11px] font-mono text-text-tertiary">
            +{extraCount}
          </span>
        )}
      </div>

      {/* Strategy + token budget */}
      <div className="flex items-center gap-3 text-xs text-text-tertiary">
        <span className="capitalize">{template.selection.strategy}</span>
        <span>·</span>
        <span className="font-mono">{(template.selection.maxTokens / 1000).toFixed(0)}K tokens</span>
      </div>
    </motion.button>
  )
}
